/**
 * HubAudio — Browser audio engine for the Banks Morgan Hub telephony.
 *
 * Transport (unchanged):
 *   Download:  GET  /api/telephony/audio/stream/{id}   (chunked response)
 *   Upload:    POST /api/telephony/audio/send/{id}     (batched every 100 ms)
 *   Status:    GET  /api/telephony/audio/status/{id}    (polled every 500 ms)
 *
 * Audio processing (upgraded):
 *   - AudioWorklet for capture and playback (replaces ScriptProcessorNode)
 *   - Anti-aliasing BiquadFilter (3.4 kHz LP) before mic downsampling
 *   - 200 ms jitter buffer on playback; silence only on genuine underrun
 *
 * Audio format on the wire: PCM 16-bit signed, 8000 Hz, mono.
 */

const TARGET_SR = 8000;
const FRAME_SAMPLES = 160;          // 20 ms at 8 kHz
const UPLOAD_INTERVAL_MS = 100;
const STATUS_POLL_MS = 500;
const JITTER_SAMPLES = 1600;        // 200 ms at 8 kHz
const DRIFT_TRIM_SAMPLES = 3200;    // 400 ms at 8 kHz — playback drift ceiling
const FADE_SAMPLES = 32;            // 4 ms fade at 8 kHz — de-click on re-buffer
const API = process.env.REACT_APP_BACKEND_URL || "";

// ──────────────── AudioWorklet source (Blob URLs) ────────────────

const CAPTURE_WORKLET_SRC = `
class HubCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ratio = sampleRate / 8000;
    this.pos = 0;
    this.out = [];
    this.tick = 0;
  }
  process(inputs) {
    const inp = inputs[0] && inputs[0][0];
    if (!inp) return true;
    while (this.pos < inp.length) {
      var idx = ~~this.pos, frac = this.pos - idx;
      var s0 = inp[idx] || 0, s1 = inp[Math.min(idx+1, inp.length-1)] || 0;
      var s = s0 + frac * (s1 - s0);
      s = s < -1 ? -1 : s > 1 ? 1 : s;
      this.out.push(s < 0 ? s * 0x8000 : s * 0x7FFF);
      this.pos += this.ratio;
    }
    this.pos -= inp.length;
    while (this.out.length >= 160) {
      var frame = new Int16Array(this.out.splice(0, 160));
      this.port.postMessage({t:'a', d:frame}, [frame.buffer]);
    }
    if (++this.tick >= 18) {
      this.tick = 0;
      var sum = 0;
      for (var i = 0; i < inp.length; i++) sum += inp[i]*inp[i];
      this.port.postMessage({t:'l', v:Math.sqrt(sum/inp.length)});
    }
    return true;
  }
}
registerProcessor('hub-capture', HubCapture);
`;

const PLAYBACK_WORKLET_SRC = `
class HubPlayback extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ring = new Float32Array(32000);
    this.wp = 0; this.rp = 0; this.buf = 0;
    this.started = false;
    this.JITTER = ${JITTER_SAMPLES};
    this.MAX_DELAY = ${DRIFT_TRIM_SAMPLES};   // 400 ms — above this we trim to catch up
    this.FADE = ${FADE_SAMPLES};              // ~4 ms ramp, avoids clicks on re-buffer
    this.fadeIn = 0;                          // fade-in samples remaining
    this.last = 0;                            // last emitted sample (fade-out anchor)
    this.port.onmessage = (e) => {
      if (e.data.t !== 'a') return;
      var d = e.data.d;
      for (var i = 0; i < d.length; i++) {
        this.ring[this.wp] = d[i] / (d[i] < 0 ? 0x8000 : 0x7FFF);
        this.wp = (this.wp + 1) % this.ring.length;
        this.buf++;
      }
      if (this.buf > this.ring.length - 256) {
        this.rp = (this.wp - this.ring.length + 256) % this.ring.length;
        if (this.rp < 0) this.rp += this.ring.length;
        this.buf = this.ring.length - 256;
      }
    };
  }
  process(inputs, outputs) {
    var out = outputs[0][0], len = out.length;
    var i, g, s;

    // ── Re-buffering: hold silence until a FULL jitter buffer has rebuilt. ──
    // Resuming on a partially-filled buffer is what produced the audible hiss:
    // it emits a few samples then zero-pads, over and over, every quantum.
    if (!this.started) {
      if (this.buf >= this.JITTER) {
        this.started = true;
        this.fadeIn = this.FADE;          // ramp back in rather than snapping on
      } else {
        if (this.last !== 0) {            // finish the fade-out, then stay silent
          for (i = 0; i < len; i++) {
            g = 1 - (i + 1) / this.FADE;
            if (g < 0) g = 0;
            out[i] = this.last * g;
          }
          this.last = 0;
        } else {
          out.fill(0);
        }
        return true;
      }
    }

    // ── Underrun: ANY low buffer is treated exactly like an empty one. ──
    if (this.buf < len) {
      this.started = false;
      for (i = 0; i < len; i++) {         // fade out instead of a hard zero-cut
        g = 1 - (i + 1) / this.FADE;
        if (g < 0) g = 0;
        out[i] = this.last * g;
      }
      this.last = 0;
      return true;
    }

    // ── Drift correction: once delay exceeds MAX_DELAY, remove exactly ONE
    //    sample this quantum so latency converges gradually rather than
    //    jumping. The sample removed is the quietest one in the upcoming
    //    window — dropping at a near-zero crossing is inaudible, whereas
    //    dropping mid-waveform leaves an audible phase click. ──
    var trimAt = -1;
    if (this.buf > this.MAX_DELAY && this.buf > len) {
      var k = this.rp, bv = 2, av;
      for (i = 0; i < len; i++) {
        av = this.ring[k];
        if (av < 0) av = -av;
        if (av < bv) { bv = av; trimAt = i; }
        k = (k + 1) % this.ring.length;
      }
      this.buf--;
    }

    for (i = 0; i < len; i++) {
      if (i === trimAt) this.rp = (this.rp + 1) % this.ring.length;
      s = this.ring[this.rp];
      this.rp = (this.rp + 1) % this.ring.length;
      if (this.fadeIn > 0) {
        g = (this.FADE - this.fadeIn) / this.FADE;
        if (g > 1) g = 1;
        this.fadeIn--;
        s = s * g;
      }
      out[i] = s;
    }
    this.buf -= len;
    this.last = out[len - 1];
    return true;
  }
}
registerProcessor('hub-playback', HubPlayback);
`;

function makeBlobURL(code) {
  const b = new Blob([code], { type: "application/javascript" });
  return URL.createObjectURL(b);
}

// ──────────────── state ────────────────
let captureCtx = null;     // native rate — for BiquadFilter + capture worklet
let playbackCtx = null;    // 8 kHz — for jitter-buffered playback worklet
let micStream = null;
let captureNode = null;    // AudioWorkletNode
let playbackNode = null;   // AudioWorkletNode
let lpFilter = null;       // BiquadFilterNode (anti-alias)
let micSource = null;

let micBuffer = [];
let uploadTimer = null;
let upFailStreak = 0;         // consecutive failed audio/send posts (bridge gone)
let reconnectingAudio = false;
let statusTimer = null;
let downloadAbort = null;
let onStatusChange = null;
let onMicLevel = null;
let _currentState = "idle";
let _gen = 0;               // FIX: identifies the live audio session
let _streamsStarted = false; // FIX: beginAudioStreams() must run once per call
let _callId = null;

function setState(s, d) {
  _currentState = s;
  // Ringback plays while we are dialling, and stops the moment anything else happens.
  if (s === "ringing" || s === "connecting") startRingback();
  else stopRingback();
  if (onStatusChange) onStatusChange(s, d);
}

// ──────────────── ringback tone (UK: 400+450 Hz, 0.4s on / 0.2s off / 0.4s on / 2s off) ────────────────

let ringCtx = null;
let ringGain = null;
let ringTimer = null;

const RING_CADENCE = 3.0;   // seconds for one full ring-ring-pause cycle

function scheduleRingCycle(startAt) {
  if (!ringGain) return;
  const g = ringGain.gain;
  // two 0.4s bursts separated by 0.2s, then 2.0s of silence
  [0, 0.6].forEach((offset) => {
    const t = startAt + offset;
    g.setValueAtTime(0.0001, t);
    g.exponentialRampToValueAtTime(0.18, t + 0.02);
    g.setValueAtTime(0.18, t + 0.38);
    g.exponentialRampToValueAtTime(0.0001, t + 0.4);
  });
}

function startRingback() {
  if (ringCtx) return;                       // already ringing
  try {
    ringCtx = new AudioContext();
    ringGain = ringCtx.createGain();
    ringGain.gain.value = 0.0001;
    ringGain.connect(ringCtx.destination);
    [400, 450].forEach((f) => {
      const osc = ringCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.connect(ringGain);
      osc.start();
    });
    scheduleRingCycle(ringCtx.currentTime + 0.05);
    ringTimer = setInterval(() => {
      if (ringCtx) scheduleRingCycle(ringCtx.currentTime + 0.05);
    }, RING_CADENCE * 1000);
  } catch (_er) {
    stopRingback();
  }
}

function stopRingback() {
  if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
  if (ringCtx) {
    try { ringCtx.close(); } catch (_er2) { /* ok */ }
    ringCtx = null;
    ringGain = null;
  }
}

// ──────────────── public API ────────────────

export async function startAudio(callId, statusCallback, micLevelCallback) {
  cleanup();                     // FIX: release any previous session first
  const gen = ++_gen;            // FIX: this call owns the session
  onStatusChange = statusCallback;
  onMicLevel = micLevelCallback || null;
  _callId = callId;
  setState("requesting_mic");

  try {
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (_e) {
    setState("error", "Microphone access denied.");
    return;
  }
  if (gen !== _gen) {            // FIX: superseded while awaiting the mic
    micStream.getTracks().forEach(t => t.stop());
    return;
  }

  // Capture context at native rate (for correct BiquadFilter + worklet downsampling)
  captureCtx = new AudioContext();

  // Playback context at 8 kHz (worklet outputs directly at target rate)
  try {
    playbackCtx = new AudioContext({ sampleRate: TARGET_SR });
  } catch (_e2) {
    playbackCtx = new AudioContext();
  }

  // Load worklet modules via Blob URLs
  const capURL = makeBlobURL(CAPTURE_WORKLET_SRC);
  const playURL = makeBlobURL(PLAYBACK_WORKLET_SRC);
  try {
    await captureCtx.audioWorklet.addModule(capURL);
    await playbackCtx.audioWorklet.addModule(playURL);
  } catch (err) {
    setState("error", "AudioWorklet failed: " + err.message);
    cleanup();
    return;
  } finally {
    URL.revokeObjectURL(capURL);
    URL.revokeObjectURL(playURL);
  }
  if (gen !== _gen) { cleanup(); return; }   // FIX: superseded while loading worklets

  setState("connecting");
  pollStatus(callId);
}

export function stopAudio() { setState("ended"); cleanup(); }
export function getAudioState() { return _currentState; }

// ──────────────── status polling (unchanged) ────────────────

function pollStatus(callId, opts) {
  if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }   // FIX
  let attempts = 0;
  let polling = false;                                                  // FIX
  statusTimer = setInterval(async () => {
    if (polling) return;         // FIX: /audio/status makes a live 3CX round-trip;
    polling = true;              // setInterval does not await, so ticks pile up
    if (++attempts > 120) { clearInterval(statusTimer); setState("no_answer"); cleanup(); return; }
    try {
      const r = await fetch(`${API}/api/telephony/audio/status/${callId}`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      if (d.state === "connected") {
        clearInterval(statusTimer); statusTimer = null;
        setState("connected");
        beginAudioStreams(callId, opts);
      } else if (d.state === "ringing") {
        setState("ringing");
      } else if (d.state === "busy") {
        clearInterval(statusTimer);
        setState("busy");
        playBusyTone();
        cleanup();
      } else if (d.state === "error" || d.state === "not_found") {
        clearInterval(statusTimer);
        setState("error", d.detail || "Call failed");
        cleanup();
      } else if (d.state === "failed") {
        clearInterval(statusTimer);
        setState("failed", d.detail || "Call failed");
        cleanup();
      }
    } catch (_e) { /* keep polling */ } finally { polling = false; }   // FIX
  }, STATUS_POLL_MS);
}

// ──────────────── audio streams ────────────────

function beginAudioStreams(callId, opts) {
  const resume = !!(opts && opts.resume);
  if (_streamsStarted && !resume) return;   // FIX: several in-flight /audio/status polls all
  if (!_streamsStarted) {                   // resolve "connected" at once - only the first wins
    _streamsStarted = true;
    setupCapturePipeline();
    setupPlaybackPipeline();
  }
  // On a resume after an audio drop the pipelines are still alive; only the
  // HTTP transports (upload POSTs / download stream) need restarting.
  startUpload(callId);
  startDownload(callId);
}

// ── Capture: mic → LP 3.4 kHz → worklet (downsample + Int16) → micBuffer ──

function setupCapturePipeline() {
  if (!captureCtx || !micStream) return;
  teardownCapture();             // FIX: fully release any previous chain

  micSource = captureCtx.createMediaStreamSource(micStream);

  // Anti-aliasing low-pass at 3.4 kHz (guard band below 4 kHz Nyquist of 8 kHz)
  lpFilter = captureCtx.createBiquadFilter();
  lpFilter.type = "lowpass";
  lpFilter.frequency.value = 3400;
  lpFilter.Q.value = 0.707; // Butterworth

  captureNode = new AudioWorkletNode(captureCtx, "hub-capture");
  captureNode.port.onmessage = (e) => {
    if (e.data.t === "a") {
      micBuffer.push(e.data.d.buffer);
    } else if (e.data.t === "l" && onMicLevel) {
      onMicLevel(e.data.v);
    }
  };

  micSource.connect(lpFilter).connect(captureNode);
  // Connect capture to a silent destination to keep the graph alive
  captureNode.connect(captureCtx.destination);
}

function teardownCapture() {   // FIX
  if (captureNode) {
    try { captureNode.port.onmessage = null; } catch (_t1) { /* ok */ }
    try { captureNode.disconnect(); } catch (_t2) { /* ok */ }
    captureNode = null;
  }
  if (lpFilter)  { try { lpFilter.disconnect(); }  catch (_t3) { /* ok */ } lpFilter = null; }
  if (micSource) { try { micSource.disconnect(); } catch (_t4) { /* ok */ } micSource = null; }
}

// ── Playback: download → worklet (jitter buffer) → speakers ──

function setupPlaybackPipeline() {
  if (!playbackCtx) return;
  playbackNode = new AudioWorkletNode(playbackCtx, "hub-playback");
  playbackNode.connect(playbackCtx.destination);
}

// ── Download: GET chunked stream → playback worklet (unchanged transport) ──

function startDownload(callId) {
  if (downloadAbort) { try { downloadAbort.abort(); } catch (_e0) { /* ok */ } }   // FIX
  downloadAbort = new AbortController();
  const gen = _gen;   // the session this download belongs to

  // The backend closes this stream when the bridge stops, which is what happens
  // when the FAR END hangs up. Without this the page never learns the call is
  // over: the duration keeps counting and the mic keeps uploading into nothing.
  const farEndHungUp = () => {
    if (gen !== _gen) return;                 // superseded, or we hung up ourselves
    if (_currentState !== "connected" && _currentState !== "reconnecting_audio") return;
    // The download stream ending used to mean "they hung up". With bridge
    // self-healing it can also be a mid-call 3CX leg drop the backend is
    // recovering from - check the call state before declaring a hangup.
    checkCallAlive(callId, 3);
  };

  const checkCallAlive = (cid, tries) => {
    fetch(`${API}/api/telephony/audio/status/${cid}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error("status " + r.status);
        const d = await r.json();
        if (d.state === "connected") {
          setState("reconnecting_audio", "Audio link dropped - reconnecting");
          startDownload(cid);
          return;
        }
        if (tries > 0 && (d.state === "calling" || d.state === "ringing")) {
          setTimeout(() => checkCallAlive(cid, tries - 1), 500);
          return;
        }
        setState("far_end_hangup");
        cleanup();
      })
      .catch(() => {
        if (tries > 0) { setTimeout(() => checkCallAlive(cid, tries - 1), 500); return; }
        setState("far_end_hangup");
        cleanup();
      });
  };

  fetch(`${API}/api/telephony/audio/stream/${callId}`, {
    credentials: "include",
    signal: downloadAbort.signal,
  })
    .then(async (resp) => {
      if (!resp.ok || !resp.body) { farEndHungUp(); return; }
      const reader = resp.body.getReader();
      let leftover = new Uint8Array(0);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value || value.byteLength === 0) continue;
        // Combine with leftover from previous read (handle odd byte boundaries)
        let data;
        if (leftover.length > 0) {
          data = new Uint8Array(leftover.length + value.byteLength);
          data.set(leftover);
          data.set(value, leftover.length);
        } else {
          data = value;
        }
        const usable = data.byteLength & ~1; // even number of bytes
        if (usable > 0) {
          // Copy into our own aligned buffer. Do NOT build an Int16Array over
          // `data.buffer` and transfer it: transferring detaches `data`, so the
          // odd trailing byte is lost and every following sample is read one
          // byte out of step (high/low swapped) - which is heard as loud
          // crackle. A stream chunk can also start at an odd byteOffset, which
          // the old form threw on.
          const i16 = new Int16Array(usable / 2);
          new Uint8Array(i16.buffer).set(data.subarray(0, usable));
          if (playbackNode) {
            playbackNode.port.postMessage({ t: "a", d: i16 }, [i16.buffer]);
          }
        }
        leftover = usable < data.byteLength ? data.slice(usable) : new Uint8Array(0);
      }
      farEndHungUp();   // reader finished => the backend closed the stream
    })
    .catch(() => { /* stream ended or aborted */ });
}

// ── Upload: batched POST every 100 ms (unchanged transport) ──

function startUpload(callId) {
  if (uploadTimer) { clearInterval(uploadTimer); uploadTimer = null; }   // FIX
  micBuffer.length = 0;                                                 // FIX
  upFailStreak = 0;
  reconnectingAudio = false;
  uploadTimer = setInterval(() => {
    if (micBuffer.length === 0) return;
    const batch = micBuffer.splice(0);
    let total = 0;
    for (const b of batch) total += b.byteLength;
    const combined = new Uint8Array(total);
    let off = 0;
    for (const b of batch) { combined.set(new Uint8Array(b), off); off += b.byteLength; }
    fetch(`${API}/api/telephony/audio/send/${callId}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      body: combined,
    })
      .then((resp) => {
        if (resp.status === 204) {
          upFailStreak = 0;
          if (reconnectingAudio && _currentState === "reconnecting_audio") {
            reconnectingAudio = false;
            setState("connected");
          }
          return;
        }
        onUploadDrop();
      })
      .catch(() => onUploadDrop());
  }, UPLOAD_INTERVAL_MS);
}

// The backend 404s audio/send once the bridge for this call is gone. The old
// code ignored that forever: the caller kept talking with their mic going
// nowhere - heard by the other side as one-way audio. React to it instead:
// first re-poll /audio/status (the backend rebuilds the bridge while the 3CX
// call is still up), then give up loudly so the caller knows to redial.
function onUploadDrop() {
  upFailStreak++;
  if (upFailStreak === 3 && !reconnectingAudio && _callId) {
    reconnectingAudio = true;
    setState("reconnecting_audio", "Audio link dropped - reconnecting");
    if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }
    pollStatus(_callId, { resume: true });
  }
  if (upFailStreak >= 45 && uploadTimer) {   // ~4.5s of continuous drops
    clearInterval(uploadTimer); uploadTimer = null;
    reconnectingAudio = false;
    setState("audio_disconnected", "Your microphone is not reaching the call - end the call and redial");
  }
}

// ──────────────── cleanup ────────────────

function cleanup() {
  _gen++;                        // FIX: abandon any in-flight startAudio
  _streamsStarted = false;       // FIX: re-arm for the next call
  stopRingback();
  if (uploadTimer) { clearInterval(uploadTimer); uploadTimer = null; }
  if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }
  if (downloadAbort) { try { downloadAbort.abort(); } catch (_e1) { /* ok */ } downloadAbort = null; }
  if (captureNode) { try { captureNode.disconnect(); } catch (_e2) { /* ok */ } captureNode = null; }
  if (lpFilter) { try { lpFilter.disconnect(); } catch (_e3) { /* ok */ } lpFilter = null; }
  if (micSource) { try { micSource.disconnect(); } catch (_e4) { /* ok */ } micSource = null; }
  if (playbackNode) { try { playbackNode.disconnect(); } catch (_e5) { /* ok */ } playbackNode = null; }
  if (captureCtx && captureCtx.state !== "closed") { try { captureCtx.close(); } catch (_e6) { /* ok */ } }
  captureCtx = null;
  if (playbackCtx && playbackCtx.state !== "closed") { try { playbackCtx.close(); } catch (_e7) { /* ok */ } }
  playbackCtx = null;
  if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null; }
  micBuffer.length = 0;          // FIX: in place, so a stale array cannot be refilled
  upFailStreak = 0;
  reconnectingAudio = false;
  _callId = null;
}

// ──────────────── DIAGNOSTIC: Mic Loopback ────────────────
let loopbackCtx = null;
let loopbackStream = null;
let loopbackSrc = null;

export async function startMicLoopback() {
  stopMicLoopback();
  loopbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  loopbackCtx = new AudioContext();
  loopbackSrc = loopbackCtx.createMediaStreamSource(loopbackStream);
  loopbackSrc.connect(loopbackCtx.destination);
  return { sampleRate: loopbackCtx.sampleRate, state: loopbackCtx.state };
}

export function stopMicLoopback() {
  if (loopbackSrc) { try { loopbackSrc.disconnect(); } catch (_el) { /* ok */ } loopbackSrc = null; }
  if (loopbackCtx) { try { loopbackCtx.close(); } catch (_el2) { /* ok */ } loopbackCtx = null; }
  if (loopbackStream) { loopbackStream.getTracks().forEach(t => t.stop()); loopbackStream = null; }
}

// ──────────────── DIAGNOSTIC: Test Tone Playback ────────────────
let toneAbort = null;

export async function playTestTone() {
  stopTestTone();
  const ctx = new AudioContext({ sampleRate: 8000 });
  toneAbort = new AbortController();

  const resp = await fetch(`${API}/api/telephony/audio/testtone`, {
    credentials: "include",
    signal: toneAbort.signal,
  });
  if (!resp.ok || !resp.body) { ctx.close(); return { error: `HTTP ${resp.status}` }; }

  // Use ScriptProcessor for diagnostic only (simple, self-contained)
  const proc = ctx.createScriptProcessor(256, 1, 1);
  const q = [];
  proc.onaudioprocess = (e) => {
    const out = e.outputBuffer.getChannelData(0);
    if (q.length > 0) {
      const c = q.shift();
      for (let i = 0; i < out.length; i++) out[i] = i < c.length ? c[i] / (c[i] < 0 ? 0x8000 : 0x7FFF) : 0;
    } else { out.fill(0); }
  };
  proc.connect(ctx.destination);

  const reader = resp.body.getReader();
  let totalBytes = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value && value.byteLength > 1) {
      totalBytes += value.byteLength;
      q.push(new Int16Array(value.buffer, value.byteOffset, Math.floor(value.byteLength / 2)));
    }
  }
  await new Promise(r => setTimeout(r, 3500));
  proc.disconnect(); ctx.close();
  return { totalBytes, status: "played" };
}

export function stopTestTone() {
  if (toneAbort) { try { toneAbort.abort(); } catch (_et) { /* ok */ } toneAbort = null; }
}

// ──────────────── UK Engaged (Busy) Tone ────────────────
// UK standard: 400 Hz, 375ms on / 375ms off, repeated 6 times (~4.5s)
let busyCtx = null;
function playBusyTone() {
  try {
    busyCtx = new AudioContext();
    const osc = busyCtx.createOscillator();
    const gain = busyCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 400;
    gain.gain.value = 0;
    osc.connect(gain).connect(busyCtx.destination);
    osc.start();
    const now = busyCtx.currentTime;
    for (let i = 0; i < 6; i++) {
      gain.gain.setValueAtTime(0.3, now + i * 0.75);
      gain.gain.setValueAtTime(0, now + i * 0.75 + 0.375);
    }
    osc.stop(now + 4.5);
    setTimeout(() => { if (busyCtx) { busyCtx.close(); busyCtx = null; } }, 5000);
  } catch (_eb) { /* ok */ }
}
